/**
 * Fabric.js object-tagging helpers for the floor plan editor — each function
 * takes the Canvas + Room (plus any external data it needs) as params
 * so it stays free of React state/closures.
 */
import { Canvas, Circle, Ellipse, FabricImage, FabricObject, FabricText, Polygon, Polyline, Rect } from 'fabric';
import { Room } from '../../types/Planner';
import {
    CHAIR_H, CHAIR_W, computeChairPositions, computeFillBadge, labelForRoom, resolveToken, roomColor,
} from './floorPlanGeometry';
import { presetFor, routeLine, stageBulge, stageTierOutlines } from './elementPresets';
import type { ElementPreset } from './elementPresets';
import { audienceCentre, crescentFacing, routeTarget } from './layoutContext';
import { crescentLabelPoint, crescentOutline } from './tableGeometry';
import type { Direction } from './tableGeometry';

// Fabric 7 changed the default object origin from left/top to center/center.
// Every coordinate in this editor (Room.position, snap-to-grid, drag sync)
// is the shape's top-left corner, so objects that do not set an origin
// explicitly must be pinned to it or they render shifted by half their size.
export const ANCHOR_TOP_LEFT = { originX: 'left', originY: 'top' } as const;

// Loads the floor plan's background image asynchronously (fabric 6+ returns a
// promise instead of taking a callback). Any previous background is dropped
// when the new one arrives, and a result that lands after the canvas was
// disposed (or after a newer load replaced it) is discarded.
const latestBackgroundLoad = new WeakMap<Canvas, string>();
export function loadBackgroundImage(fc: Canvas, url: string) {
    latestBackgroundLoad.set(fc, url);
    FabricImage.fromURL(url)
        .then(img => {
            if (fc.disposed || latestBackgroundLoad.get(fc) !== url) return;
            fc.getObjects()
                .filter(o => (o as FabricObject & { isBackgroundImage?: boolean }).isBackgroundImage)
                .forEach(o => fc.remove(o));
            img.set({ ...ANCHOR_TOP_LEFT, left: 0, top: 0, selectable: false, evented: false, opacity: 0.5 });
            (img as FabricImage & { isBackgroundImage: boolean }).isBackgroundImage = true;
            fc.add(img);
            fc.sendObjectToBack(img);
            fc.renderAll();
        })
        .catch(() => { /* unreachable image: keep the plan usable without a backdrop */ });
}

// Chairs are plain decorative Rects, not selectable/evented (matching the
// legacy DOM editor, where chairs were never independently draggable).
// Tagged with chairForRoomId rather than roomId so the many `.find(o =>
// o.roomId === id)` lookups elsewhere (layer ordering, drag sync) keep
// resolving to the table shape itself, not a chair.
export function addChairsToCanvas(fc: Canvas, room: Room, facing: Direction = 'up') {
    for (const pos of computeChairPositions(room, facing)) {
        const chair = new Rect({
            left: pos.left,
            top: pos.top,
            width: CHAIR_W,
            height: CHAIR_H,
            originX: 'center',
            originY: 'center',
            angle: (pos.angleRad * 180) / Math.PI,
            fill: '#5c4433',
            selectable: false,
            evented: false,
        }) as Rect & { chairForRoomId: string };
        chair.chairForRoomId = room.id;
        fc.add(chair);
    }
}

export function removeChairsForRoom(fc: Canvas, roomId: string) {
    fc.getObjects()
        .filter(o => (o as FabricObject & { chairForRoomId?: string }).chairForRoomId === roomId)
        .forEach(o => fc.remove(o));
}

// Applies lock behaviour (migration gap 5/11) to a room's interactive
// shape: still visible/selectable, but drag/resize is disabled.
export function applyLockState(obj: FabricObject, locked: boolean) {
    obj.set({
        lockMovementX: locked,
        lockMovementY: locked,
        lockScalingX: locked,
        lockScalingY: locked,
        // Rotation is never saved (the zones table has no angle column), so the
        // free-rotate handle is hidden for everything; the toolbar's quarter-turn
        // button swaps width and height, which is saved.
        lockRotation: true,
        hasControls: !locked,
        opacity: locked ? 0.75 : 1,
    });
    obj.setControlVisible?.('mtr', false);
}

// ── Fill badges, lock icon, status dot, named seats (migration gaps 1/2/5) ──
// Decorations are separate untracked-by-React-state fabric objects (like
// chairs and labels), tagged with `decorationForRoomId` so they can be
// cleared and rebuilt whenever a room's data changes.
export function removeDecorationsForRoom(fc: Canvas, roomId: string) {
    fc.getObjects()
        .filter(o => (o as FabricObject & { decorationForRoomId?: string }).decorationForRoomId === roomId)
        .forEach(o => fc.remove(o));
}

export function addDecorationsToCanvas(
    fc: Canvas,
    room: Room,
    guestGroupCounts: Record<string, number>,
    tableStatusMap: Record<string, 'empty' | 'partial' | 'full'>,
    facing: Direction = 'up',
) {
    if (room.type !== 'table') return;
    const cx = room.position.x;
    const cy = room.position.y;

    // Fill badge — "filled/total" — top-left of the table
    const badge = computeFillBadge(room, guestGroupCounts);
    if (badge.show && !room.locked) {
        const text = new FabricText(`${badge.filled}/${badge.total}`, {
            left: cx + 4, top: cy + 3,
            fontSize: 9, fontWeight: 700, fill: '#1f2937',
            backgroundColor: 'rgba(255,255,255,0.9)',
            originX: 'left', originY: 'top',
            selectable: false, evented: false,
        }) as FabricText & { decorationForRoomId: string };
        text.decorationForRoomId = room.id;
        fc.add(text);
    }

    // Status dot — bottom-right — reflects the empty/partial/full seating state
    const status = tableStatusMap[room.id];
    if (status === 'partial' || status === 'full') {
        const dotColor = status === 'full' ? '#22c55e' : '#f59e0b';
        const dot = new Circle({
            ...ANCHOR_TOP_LEFT,
            left: cx + room.width - 8, top: cy + room.height - 8,
            radius: 4, fill: dotColor, stroke: 'rgba(255,255,255,0.8)', strokeWidth: 1,
            selectable: false, evented: false,
        }) as Circle & { decorationForRoomId: string };
        dot.decorationForRoomId = room.id;
        fc.add(dot);
    }

    // Lock icon — top-left — replaces the fill badge position when locked
    if (room.locked) {
        const lockIcon = new FabricText('\u{1F512}', {
            left: cx + 4, top: cy + 2,
            fontSize: 10,
            originX: 'left', originY: 'top',
            selectable: false, evented: false,
        }) as FabricText & { decorationForRoomId: string };
        lockIcon.decorationForRoomId = room.id;
        fc.add(lockIcon);
    }

    // Head-table star marker — top-right
    if (room.isHeadTable) {
        const star = new FabricText('★', {
            left: cx + room.width - 4, top: cy + 2,
            fontSize: 10, fill: '#f59e0b',
            originX: 'right', originY: 'top',
            selectable: false, evented: false,
        }) as FabricText & { decorationForRoomId: string };
        star.decorationForRoomId = room.id;
        fc.add(star);
    }

    // Named seats on head tables (migration gap 2/11) — a small label
    // next to each chair showing the assigned guest's first name, not
    // just an anonymous seat circle.
    if (room.isHeadTable && room.namedSeats && room.namedSeats.length > 0) {
        const positions = computeChairPositions(room, facing);
        for (const seat of room.namedSeats) {
            const pos = positions[seat.seatIndex];
            if (!pos || !seat.guestName.trim()) continue;
            const firstName = seat.guestName.trim().split(/\s+/)[0];
            const label = new FabricText(firstName, {
                left: pos.left, top: pos.top + CHAIR_H,
                fontSize: 7, fill: '#3f2a1c', fontWeight: 600,
                originX: 'center', originY: 'top',
                selectable: false, evented: false,
            }) as FabricText & { decorationForRoomId: string };
            label.decorationForRoomId = room.id;
            fc.add(label);
        }
    }
}

// ── Fill, labels and bodies ───────────────────────────────────────────────
// Some rooms are drawn by a separate "body" (the crescent arc, the stage tiers,
// the route line) and keep a see-through box on top purely for selecting,
// moving and resizing. Their box must stay transparent.
function usesBody(room: Room): boolean {
    if (room.type === 'table') return room.tableShape === 'crescent';
    const kind = presetFor(room.type)?.kind;
    return kind === 'stage-tiers' || kind === 'route' || kind === 'section';
}

/** Fill of the selectable shape: transparent when a body or outline draws the room. */
export function shapeFillFor(room: Room): string {
    if (usesBody(room) && room.type !== 'vip_entrance') return 'transparent';
    return roomColor(room.type, room);
}

export interface LabelStyle {
    text: string;
    left: number;
    top: number;
    angle: number;
    fontSize: number;
    fill: string;
    originX: 'center';
    originY: 'center' | 'bottom' | 'top';
    fontWeight: string;
    opacity: number;
}

/** Everything about a room's label that depends on the room: one source for create and update. */
export function labelStyleFor(room: Room, rooms: ReadonlyArray<Room>): LabelStyle {
    const cx = room.position.x + room.width / 2;
    const cy = room.position.y + room.height / 2;
    const base: LabelStyle = {
        text: labelForRoom(room, rooms), left: cx, top: cy, angle: 0, fontSize: 11,
        fill: resolveToken('--text-dark', 'rgb(45, 31, 20)'), originX: 'center', originY: 'center',
        fontWeight: 'bold', opacity: 1,
    };
    if (room.type === 'table') {
        const white = resolveToken('--bg-white', 'rgb(255, 255, 255)');
        if (room.tableShape === 'crescent') {
            const facing = crescentFacing(room, rooms);
            const p = crescentLabelPoint({ x: room.position.x, y: room.position.y, width: room.width, height: room.height }, facing);
            return { ...base, left: p.x, top: p.y, fontSize: 11, fill: white };
        }
        return { ...base, fontSize: room.tableShape === 'rect' || room.tableShape === 'rectangular' ? 11 : 12, fill: white };
    }
    const preset = presetFor(room.type);
    const kind = preset?.kind;
    if (kind === 'section') {
        const size = Math.max(24, Math.min(96, Math.min(room.width, room.height) * 0.35));
        return { ...base, fontSize: size, fill: resolveToken('--text-muted', 'rgb(107, 91, 75)'), opacity: 0.35 };
    }
    if (kind === 'route') {
        return { ...base, top: room.position.y - 2, originY: 'bottom', fontSize: 10 };
    }
    if (room.height > room.width * 2) {
        return { ...base, angle: -90, fontSize: room.width < 24 ? 9 : 11 };
    }
    // A whole-hall sized zone is a container: its name goes on its top edge so
    // the tables and floor inside it are not written over.
    if (!preset && room.width >= 600 && room.height >= 400) {
        return { ...base, top: room.position.y + 6, originY: 'top' };
    }
    return base;
}

export function removeBodiesForRoom(fc: Canvas, roomId: string) {
    fc.getObjects()
        .filter(o => (o as FabricObject & { bodyForRoomId?: string }).bodyForRoomId === roomId)
        .forEach(o => fc.remove(o));
}

function tagBody<T extends FabricObject>(obj: T, roomId: string): T {
    (obj as T & { bodyForRoomId?: string }).bodyForRoomId = roomId;
    obj.set({ selectable: false, evented: false });
    return obj;
}

const toPairs = (pts: { x: number; y: number }[]) => pts.map(p => ({ x: p.x, y: p.y }));

/** Visual bodies for a room, bottom to top. Empty for rooms drawn by their own shape. */
function buildBodies(room: Room, rooms: ReadonlyArray<Room>): FabricObject[] {
    const box = { x: room.position.x, y: room.position.y, width: room.width, height: room.height };
    const stroke = resolveToken('--border-dark', 'rgb(160, 140, 120)');
    if (room.type === 'table' && room.tableShape === 'crescent') {
        const pts = crescentOutline(box, crescentFacing(room, rooms));
        return [new Polygon(toPairs(pts), {
            fill: roomColor(room.type, room), stroke: 'rgba(255,255,255,0.8)', strokeWidth: 2, objectCaching: false,
        })];
    }
    const preset = presetFor(room.type);
    if (preset?.kind === 'stage-tiers') {
        const fill = roomColor(room.type, room);
        const bulge = stageBulge(box, audienceCentre(rooms));
        return stageTierOutlines(box, bulge).map((pts, k, all) => new Polygon(toPairs(pts), {
            fill, stroke, strokeWidth: 1.5, opacity: 0.45 + (0.55 * k) / Math.max(1, all.length - 1), objectCaching: false,
        }));
    }
    if (preset?.kind === 'route') {
        const r = routeLine(box, routeTarget(room, rooms));
        const line = resolveToken('--success-text', 'rgb(22, 101, 52)');
        return [
            new Polyline([r.from, r.to], { fill: 'transparent', stroke: line, strokeWidth: 3, strokeDashArray: [8, 5], objectCaching: false }),
            new Polyline([r.head[0], r.to, r.head[1]], { fill: 'transparent', stroke: line, strokeWidth: 3, objectCaching: false }),
        ];
    }
    return [];
}

/** (Re)draw a room's bodies directly beneath its selectable shape. */
export function refreshBodiesForRoom(fc: Canvas, room: Room, rooms: ReadonlyArray<Room>) {
    removeBodiesForRoom(fc, room.id);
    const shape = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === room.id);
    for (const body of buildBodies(room, rooms)) {
        tagBody(body, room.id);
        fc.add(body);
        if (shape) fc.moveObjectTo(body, fc.getObjects().indexOf(shape));
    }
}

/** After any reorder: keep each body immediately under its own shape. */
export function reassertBodiesBelowShapes(fc: Canvas) {
    const objects = fc.getObjects();
    const bodies = objects.filter(o => (o as FabricObject & { bodyForRoomId?: string }).bodyForRoomId);
    for (const body of bodies) {
        const id = (body as FabricObject & { bodyForRoomId?: string }).bodyForRoomId;
        const shape = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === id);
        if (!shape) continue;
        const shapeIdx = fc.getObjects().indexOf(shape);
        const bodyIdx = fc.getObjects().indexOf(body);
        fc.moveObjectTo(body, bodyIdx < shapeIdx ? shapeIdx - 1 : shapeIdx);
    }
}

function addLabel(fc: Canvas, room: Room, rooms: ReadonlyArray<Room>) {
    const style = labelStyleFor(room, rooms);
    const label = new FabricText(style.text, {
        ...style, selectable: false, evented: false,
    }) as FabricText & { labelForRoomId?: string };
    label.labelForRoomId = room.id;
    fc.add(label);
}

function strokeFor(preset: ElementPreset) {
    return {
        stroke: resolveToken('--border-dark', 'rgb(160, 140, 120)'),
        strokeWidth: preset.kind === 'section' ? 2 : 1.5,
        strokeDashArray: preset.dashed ? [8, 6] : undefined,
        strokeUniform: true,
    };
}

/** Facing of a crescent's hollow side, for callers that only have the plan's rooms. */
export function facingFor(room: Room, rooms: ReadonlyArray<Room>): Direction {
    return room.tableShape === 'crescent' ? crescentFacing(room, rooms) : 'up';
}

export function addRoomToCanvas(
    fc: Canvas,
    room: Room,
    guestGroupCounts: Record<string, number>,
    tableStatusMap: Record<string, 'empty' | 'partial' | 'full'>,
    allRooms: ReadonlyArray<Room> = [room],
) {
    const isTable = room.type === 'table';
    const color = roomColor(room.type, room);
    const preset = isTable ? undefined : presetFor(room.type);
    const ellipseTable = isTable && (room.tableShape === undefined || room.tableShape === 'round' || room.tableShape === 'oval');
    const ellipseElement = preset?.kind === 'ellipse';

    if (ellipseTable || ellipseElement) {
        const circle = new Ellipse({
            ...ANCHOR_TOP_LEFT,
            left: room.position.x,
            top:  room.position.y,
            rx: room.width / 2,
            ry: room.height / 2,
            fill: color,
            ...(preset ? strokeFor(preset) : { stroke: 'rgba(255,255,255,0.8)', strokeWidth: 2 }),
        }) as Ellipse & { roomId: string; roomType: string; tableShapeType?: Room['tableShape']; seatCount?: number };
        circle.roomId   = room.id;
        circle.roomType = room.type;
        if (isTable) {
            circle.tableShapeType = room.tableShape;
            circle.seatCount = room.seats ?? room.capacity;
        }
        applyLockState(circle, room.locked ?? false);
        fc.add(circle);
        addLabel(fc, room, allRooms);
    } else {
        const rect = new Rect({
            ...ANCHOR_TOP_LEFT,
            left: room.position.x,
            top:  room.position.y,
            width:  room.width,
            height: room.height,
            fill:   shapeFillFor(room),
            ...(preset ? strokeFor(preset) : { stroke: usesBody(room) ? 'transparent' : 'rgba(255,255,255,0.8)', strokeWidth: 2 }),
            rx: preset ? 3 : 6, ry: preset ? 3 : 6,
            // A section is a large see-through region: only its outline is clickable,
            // so tables inside it stay selectable.
            perPixelTargetFind: preset?.kind === 'section',
            targetFindTolerance: 6,
        }) as Rect & { roomId: string; roomType: string; tableShapeType?: Room['tableShape']; seatCount?: number };
        rect.roomId   = room.id;
        rect.roomType = room.type;
        if (isTable) {
            rect.tableShapeType = room.tableShape;
            rect.seatCount = room.seats ?? room.capacity;
        }
        applyLockState(rect, room.locked ?? false);
        fc.add(rect);
        refreshBodiesForRoom(fc, room, allRooms);
        if (isTable || room.name) addLabel(fc, room, allRooms);
    }

    if (isTable) {
        addChairsToCanvas(fc, room, facingFor(room, allRooms));
    }
    addDecorationsToCanvas(fc, room, guestGroupCounts, tableStatusMap, facingFor(room, allRooms));
}

// ── Layer ordering ────────────────────────────────────────────────────────
// Labels are non-interactive text overlays — keep each one directly above
// its OWN shape, not globally bringToFront'd above every other shape on
// the canvas. A blanket bringToFront meant a zone's name label kept
// rendering on top even after another zone was deliberately brought in
// front of it — the label always won regardless of the shape's actual
// z-order, which reads as "text bleeding through" a supposedly-frontmost
// element.
export function reassertLabelsOnTop(fc: Canvas) {
    const objects = fc.getObjects();
    objects
        .filter(o => o instanceof FabricText)
        .forEach(label => {
            const roomId = (label as FabricObject & { labelForRoomId?: string; decorationForRoomId?: string })
                .labelForRoomId ?? (label as FabricObject & { decorationForRoomId?: string }).decorationForRoomId;
            const shape = roomId ? objects.find(o => (o as FabricObject & { roomId?: string }).roomId === roomId) : undefined;
            if (shape) {
                fc.moveObjectTo(label, fc.getObjects().indexOf(shape) + 1);
            } else {
                fc.bringObjectToFront(label);
            }
        });
}

// The background image must never end up above a zone/table, or sendToBack
// on a shape would visually vanish it under the (opaque-ish) background.
export function pinBackgroundToBack(fc: Canvas) {
    const bg = fc.getObjects().find(o => (o as FabricImage & { isBackgroundImage?: boolean }).isBackgroundImage);
    if (bg) fc.sendObjectToBack(bg);
}

// Chairs must stay visually above their own table, or sending the table
// backward (e.g. under an overlapping zone) buries its chairs too since
// they don't move together automatically.
export function reassertChairsAboveTables(fc: Canvas) {
    fc.getObjects().filter(o => (o as FabricObject & { chairForRoomId?: string }).chairForRoomId).forEach(o => fc.bringObjectToFront(o));
}

// Badges/lock icons/status dots must stay visible above their table, or
// reordering a table backward would bury its fill badge under a zone.
export function reassertDecorationsAboveTables(fc: Canvas) {
    fc.getObjects().filter(o => (o as FabricObject & { decorationForRoomId?: string }).decorationForRoomId).forEach(o => fc.bringObjectToFront(o));
}
